import { createElement } from 'react';
import { hydrateRoot, createRoot } from 'react-dom/client';
import { getStoreAndRouterHistory } from '../modules/redux-client';
import { WrapClientComponent } from '../utils/wrappers-client.util';
const rootMap = new Map();
const pendingUnmounts = new Set();
/**
 * Completes a deferred unmount. The node stays in `rootMap` until this runs, so a hydration pass
 * landing inside the deferral window still sees the live root instead of mounting a second one on
 * top of it.
 */
const flushUnmount = (node) => {
    if (!pendingUnmounts.delete(node))
        return;
    const root = rootMap.get(node);
    rootMap.delete(node);
    root?.unmount();
};
/**
 * Renders a server-rendered node into a React root and returns the node when a root was actually
 * created or re-rendered, so callers can tell which nodes they own. Returns `undefined` when this
 * call mounted nothing.
 */
export const RenderClientComponent = async (scene, renderData, node, modules) => {
    if (!renderData?.context)
        return;
    // A dispose is queued for this node; finish it now so the decision below is made against the
    // real state of the node rather than a root that is about to disappear.
    flushUnmount(node);
    // Already hydrated by an earlier pass. Re-hydrating would unmount the existing root, which empties
    // the DOM node, and hydrateRoot on an empty container degrades into a full client render.
    if (rootMap.has(node) && !scene.clientOnly)
        return;
    modules.numeral.changeLocal(renderData.context.aemConfiguration.locale);
    const storeAndRouterHistory = await getStoreAndRouterHistory(scene, renderData, modules.redux);
    const wrappedComponent = await WrapClientComponent(scene, renderData, modules.i18n, storeAndRouterHistory);
    // Re-read after the awaits: another pass may have mounted this node in the meantime.
    const existingRoot = rootMap.get(node);
    if (existingRoot) {
        if (!scene.clientOnly)
            return;
        // Client-only scenes are allowed to re-render, but createRoot must not run twice on the same
        // container.
        existingRoot.render(createElement(wrappedComponent));
        return node;
    }
    if (scene.clientOnly) {
        const root = createRoot(node);
        rootMap.set(node, root);
        root.render(createElement(wrappedComponent));
    }
    else {
        rootMap.set(node, hydrateRoot(node, createElement(wrappedComponent)));
    }
    return node;
};
/**
 * Tears down a root created by RenderClientComponent. Scoped Hydrater callers (RowBanner, product
 * placement copy) must call this when their container goes away, otherwise the root, its component
 * tree and the detached DOM stay referenced by rootMap for the lifetime of the page.
 *
 * Deferred to a microtask because callers unmount from a React effect cleanup, and React warns when
 * a root is unmounted synchronously while another tree is still rendering.
 */
export const UnmountClientComponent = (node) => {
    if (!rootMap.has(node) || pendingUnmounts.has(node))
        return;
    pendingUnmounts.add(node);
    queueMicrotask(() => flushUnmount(node));
};
